import { FormEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  createTopic,
  deleteTopic,
  errorMessage,
  fetchHashtags,
  fetchLeaderboard,
  fetchTrendSources,
  fetchTopics,
  runTopic,
  setTopicActive,
  topicBreakdown,
  topicContent,
  TopicActivity,
} from './api';
import TopicIntelligence from './TopicIntelligence';
import { trendSourceLabel } from './trendSources';
import { SignalComparison, SignalPlot } from './MarketVisuals';

const numFmt = new Intl.NumberFormat('it-IT');
const dtFmt = new Intl.DateTimeFormat('it-IT', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/** Fase del momentum: combina crescita e accelerazione in un'etichetta leggibile. */
const STAGE: Record<string, { label: string; cls: string }> = {
  ACCELERATING: { label: 'Accelera', cls: 'growth-up' },
  RISING: { label: 'Cresce', cls: 'growth-up' },
  STEADY: { label: 'Stabile', cls: '' },
  COOLING: { label: 'Rallenta', cls: 'growth-down' },
  FLAT: { label: 'Fermo', cls: '' },
};

function percentage(current: number, previous: number): number | null {
  if (previous <= 0) return current > 0 ? null : 0;
  return Math.round(((current - previous) / previous) * 100);
}

function sourceNames(value: string): string {
  const labels = value.split(',').map((source) => source.trim()).filter(Boolean).map(trendSourceLabel);
  return labels.length > 0 ? labels.join(' · ') : 'Fonti in aggiornamento';
}

function Momentum({ stage, current, previous }: { stage: string; current: number; previous: number }) {
  const s = STAGE[stage] ?? STAGE.FLAT;
  const max = Math.max(1, current, previous);
  const delta = percentage(current, previous);
  return (
    <span className="momentum-visual" title={`${current} contenuti negli ultimi 7 giorni, ${previous} nei 7 precedenti`}>
      <span className="trend-bars" aria-hidden="true"><span style={{ width: `${Math.max(5, previous / max * 100)}%` }} /><span style={{ width: `${Math.max(5, current / max * 100)}%` }} /></span>
      <span className={`growth ${s.cls}`}>{delta == null ? 'Nuovo' : `${delta > 0 ? '+' : ''}${delta}%`}<small>{s.label}</small></span>
    </span>
  );
}

/** Dettaglio di un topic: breakdown cross-source + contenuti + gestione. */
function TopicDetail({ row, sources }: { row: TopicActivity; sources: string }) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['trend-topics'] });
    queryClient.invalidateQueries({ queryKey: ['trend-leaderboard'] });
    queryClient.invalidateQueries({ queryKey: ['trend-breakdown', row.topicId] });
    queryClient.invalidateQueries({ queryKey: ['trend-content', row.topicId] });
    queryClient.invalidateQueries({ queryKey: ['topic-themes', row.topicId] });
    queryClient.invalidateQueries({ queryKey: ['topic-dedup', row.topicId] });
    queryClient.invalidateQueries({ queryKey: ['topic-forecast', row.topicId] });
  };
  const breakdown = useQuery({
    queryKey: ['trend-breakdown', row.topicId],
    queryFn: () => topicBreakdown(row.topicId),
  });
  const content = useInfiniteQuery({
    queryKey: ['trend-content', row.topicId],
    queryFn: ({ pageParam }) => topicContent(row.topicId, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.last ? undefined : last.number + 1),
    placeholderData: keepPreviousData,
  });
  const run = useMutation({ mutationFn: runTopic, onSuccess: invalidate });
  const toggle = useMutation({
    mutationFn: (v: boolean) => setTopicActive(row.topicId, v),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: deleteTopic, onSuccess: invalidate });

  const items = content.data?.pages.flatMap((p) => p.content) ?? [];

  return (
    <div className="topic-detail">
      <p className="page-actions">
        <button type="button" className="link-btn" disabled={run.isPending} onClick={() => run.mutate(row.topicId)}>
          {run.isPending ? 'Raccolgo…' : 'Esegui ora'}
        </button>
        <button type="button" className="link-btn" onClick={() => toggle.mutate(!row.active)}>
          {row.active ? 'Sospendi' : 'Riattiva'}
        </button>
        <button
          type="button"
          className="link-btn link-danger"
          onClick={() => {
            if (window.confirm(`Eliminare il monitoraggio «${row.query}»?`)) remove.mutate(row.topicId);
          }}
        >
          Elimina
        </button>
      </p>
      {sources.split(',').includes('reddit') && <p className="source-cost-note"><span className="source-cost-badge">Apify</span> Questo monitoraggio include Reddit e usa credito a ogni esecuzione.</p>}

      {breakdown.isSuccess && breakdown.data.length > 0 && (
        <p className="ad-meta">
          {breakdown.data.map((b) => (
            <span key={b.source}>
              <strong>{trendSourceLabel(b.source)}</strong>: {numFmt.format(b.count)}{b.source === 'reddit' && <span className="source-cost-badge">Apify</span>}
            </span>
          ))}
        </p>
      )}

      <TopicIntelligence topicId={row.topicId} />

      <div className="feed">
        {items.slice(0, 15).map((c) => (
          <article key={c.id} className="trend-item">
            <p className="ad-meta">
              <span className="trend-source">{trendSourceLabel(c.source)}{c.source === 'reddit' && <span className="source-cost-badge">Apify</span>}</span>
              {c.authorName && <span>{c.authorName}</span>}
              {c.publishedAt && <span>{dtFmt.format(new Date(c.publishedAt))}</span>}
            </p>
            {c.sourceUrl ? (
              <a className="trend-title" href={c.sourceUrl} target="_blank" rel="noopener noreferrer">
                {c.title || c.text?.slice(0, 120) || '(senza titolo)'}
              </a>
            ) : (
              <span className="trend-title">{c.title || c.text?.slice(0, 120) || '(senza titolo)'}</span>
            )}
          </article>
        ))}
      </div>
      {content.hasNextPage && (
        <button type="button" className="btn-more" disabled={content.isFetchingNextPage} onClick={() => content.fetchNextPage()}>
          {content.isFetchingNextPage ? 'Carico…' : 'Carica altri contenuti'}
        </button>
      )}
      {(run.isError || toggle.isError || remove.isError) && (
        <p className="error">{errorMessage(run.error ?? toggle.error ?? remove.error)}</p>
      )}
    </div>
  );
}

/** Trend radar: classifica dei temi come prima vista, poi dettaglio e gestione. */
export default function Trend() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const requestedTopic = params.get('topic');
  const leaderboard = useQuery({ queryKey: ['trend-leaderboard'], queryFn: fetchLeaderboard });
  const [allTags, setAllTags] = useState(false);
  const hashtags = useQuery({
    queryKey: ['trend-hashtags', allTags],
    queryFn: () => fetchHashtags(allTags ? 300 : 30),
    placeholderData: keepPreviousData,
  });
  const topics = useQuery({ queryKey: ['trend-topics'], queryFn: fetchTopics, staleTime: 60_000 });
  const availableSources = useQuery({ queryKey: ['trend-sources'], queryFn: fetchTrendSources, staleTime: 300_000 });

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<string | null>(() => requestedTopic);
  const view: 'topics' | 'hashtags' = requestedTopic || params.get('view') !== 'hashtags' ? 'topics' : 'hashtags';
  const [selectedSources, setSelectedSources] = useState<string[] | null>(null);

  useEffect(() => {
    if (!requestedTopic) return;
    setOpen(requestedTopic);
  }, [requestedTopic]);

  useEffect(() => {
    if (!requestedTopic || !leaderboard.isSuccess) return;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(`topic-${requestedTopic}`)?.scrollIntoView({ block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [leaderboard.isSuccess, requestedTopic]);

  useEffect(() => {
    if (selectedSources === null && availableSources.data) {
      setSelectedSources(availableSources.data.filter((source) => source !== 'reddit'));
    }
  }, [availableSources.data, selectedSources]);

  const create = useMutation({
    mutationFn: () => createTopic(query.trim(), (selectedSources ?? []).join(',')),
    onSuccess: (t) => {
      setQuery('');
      setOpen(t.id);
      queryClient.invalidateQueries({ queryKey: ['trend-leaderboard'] });
      queryClient.invalidateQueries({ queryKey: ['trend-topics'] });
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (query.trim() && (selectedSources?.length ?? 0) > 0) create.mutate();
  }

  function toggleSource(source: string) {
    setSelectedSources((current) => {
      const rows = current ?? [];
      return rows.includes(source) ? rows.filter((item) => item !== source) : [...rows, source];
    });
  }

  function changeView(nextView: 'topics' | 'hashtags') {
    const next = new URLSearchParams(params);
    next.delete('topic');
    if (nextView === 'hashtags') next.set('view', 'hashtags');
    else next.delete('view');
    setParams(next);
    setOpen(null);
  }

  const rows = leaderboard.data ?? [];
  const topicSources = new Map((topics.data ?? []).map((topic) => [topic.id, topic.sources]));
  const leading = rows[0] ?? null;
  const leadingDelta = leading ? percentage(leading.items7, leading.itemsPrev) : null;
  const currentTotal = rows.reduce((sum, row) => sum + row.items7, 0);
  const previousTotal = rows.reduce((sum, row) => sum + row.itemsPrev, 0);
  const totalDelta = percentage(currentTotal, previousTotal);
  const volumeMax = Math.max(1, currentTotal, previousTotal);

  return (
    <div className="radar-page">
      <header className="page-intro radar-heading"><div><p className="eyebrow">Segnali di mercato</p><h1>Radar</h1><p className="page-description">Scopri quali temi e hashtag stanno accelerando negli ultimi 7 giorni.</p></div></header>
      <nav className="segmented-control" aria-label="Vista Radar">
        <button type="button" className={view === 'topics' ? 'is-active' : ''} aria-pressed={view === 'topics'} onClick={() => changeView('topics')}>Temi</button>
        <button type="button" className={view === 'hashtags' ? 'is-active' : ''} aria-pressed={view === 'hashtags'} onClick={() => changeView('hashtags')}>Hashtag</button>
      </nav>

      {view === 'topics' && <section className="radar-section">
        {leading && <section className="radar-overview" aria-labelledby="radar-overview-title">
          <header><div><p className="eyebrow">Andamento complessivo</p><h2 id="radar-overview-title">{totalDelta == null ? 'Nuovi segnali' : `${totalDelta > 0 ? '+' : ''}${totalDelta}%`}<small> rispetto ai 7 giorni precedenti</small></h2></div><span>Ultimi 7 giorni</span></header>
          <div className="radar-visual-summary"><SignalComparison previous={previousTotal} current={currentTotal} label="Segnali negli ultimi 7 giorni" /><SignalPlot points={rows.map((row) => ({ label: row.query, value: row.items7 }))} label="Volume per tema monitorato" /></div>
          <div className="radar-volume-bars" aria-label={`${currentTotal} contenuti negli ultimi 7 giorni, ${previousTotal} nei 7 precedenti`}>
            <div><span>7 giorni precedenti</span><i><b style={{ width: `${Math.max(4, previousTotal / volumeMax * 100)}%` }}/></i><strong>{numFmt.format(previousTotal)}</strong></div>
            <div className="is-current"><span>Ultimi 7 giorni</span><i><b style={{ width: `${Math.max(4, currentTotal / volumeMax * 100)}%` }}/></i><strong>{numFmt.format(currentTotal)}</strong></div>
          </div>
          <footer><span>Segnale più forte</span><strong>{leading.query}</strong><small>{leadingDelta == null ? 'Nuovo nel periodo' : `${leadingDelta > 0 ? '+' : ''}${leadingDelta}%`} · {sourceNames(topicSources.get(leading.topicId) ?? '')}</small></footer>
        </section>}
        <div className="section-heading"><div><h2>In accelerazione</h2><p>Volume, diffusione e fonti dei temi monitorati</p></div><span>{rows.length} monitorati</span></div>
        {leaderboard.isPending && <div className="radar-loading" role="status"><span className="sr-only">Caricamento temi</span>{[0, 1, 2].map((item) => <div key={item} className="radar-skeleton-row" aria-hidden="true"><span/><span/></div>)}</div>}
        {leaderboard.isSuccess && rows.length === 0 && <section className="empty empty-compact"><h2>Nessun tema monitorato</h2><p>Aggiungi un tema per iniziare a confrontarne l’attività.</p></section>}
        <div className="radar-list">
          {rows.map((topic, index) => <article id={`topic-${topic.topicId}`} key={topic.topicId} className={topic.active ? 'radar-row' : 'radar-row is-paused'}>
            <button type="button" className="radar-row-toggle" aria-expanded={open === topic.topicId} onClick={() => setOpen(open === topic.topicId ? null : topic.topicId)}>
              <span className="rank">{index + 1}</span><span className="radar-row-title"><strong>{topic.query}</strong><small>{sourceNames(topicSources.get(topic.topicId) ?? '')} · {numFmt.format(topic.items7)} contenuti · {numFmt.format(topic.authors7)} autori</small></span><Momentum stage={topic.stage} current={topic.items7} previous={topic.itemsPrev} /><span aria-hidden="true">{open === topic.topicId ? '−' : '+'}</span>
            </button>
            {open === topic.topicId && <TopicDetail row={topic} sources={topicSources.get(topic.topicId) ?? ''} />}
          </article>)}
        </div>
        <section className="topic-create" id="new-topic"><h2>Monitora un tema</h2><form onSubmit={onSubmit}><div className="search-line"><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Es. AI agents" aria-label="Nuovo tema"/><button type="submit" disabled={create.isPending || !query.trim() || (selectedSources?.length ?? 0) === 0}>{create.isPending ? 'Raccolgo…' : 'Aggiungi'}</button></div><details className="topic-source-picker"><summary><span>Fonti</span><strong>{selectedSources?.length ?? 0} selezionate</strong></summary>{availableSources.isPending ? <p className="muted">Caricamento fonti…</p> : availableSources.isError ? <div className="notice notice-error"><p>{errorMessage(availableSources.error)}</p><button type="button" className="btn-secondary" onClick={() => availableSources.refetch()}>Riprova</button></div> : <fieldset><legend className="sr-only">Fonti del nuovo monitoraggio</legend>{(availableSources.data ?? []).map((source) => <label className={(selectedSources ?? []).includes(source) ? 'source-choice is-selected' : 'source-choice'} key={source}><input type="checkbox" checked={(selectedSources ?? []).includes(source)} onChange={() => toggleSource(source)} /><span>{trendSourceLabel(source)}{source === 'reddit' && <span className="source-cost-badge">Apify</span>}</span></label>)}</fieldset>}{(selectedSources ?? []).includes('reddit') && <p className="source-cost-note"><span className="source-cost-badge">Apify</span> Reddit consuma credito a ogni esecuzione del topic.</p>}</details></form>{create.isError && <p className="error">{errorMessage(create.error)}</p>}</section>
      </section>}

      {view === 'hashtags' && <section className="radar-section">
        <div className="section-heading"><div><h2>Hashtag emergenti</h2><p>Volume raccolto da TikTok e dalle altre fonti</p></div></div>
        {hashtags.isPending && <div className="radar-loading" role="status"><span className="sr-only">Caricamento hashtag</span>{[0, 1, 2].map((item) => <div key={item} className="radar-skeleton-row" aria-hidden="true"><span/><span/></div>)}</div>}
        {hashtags.isSuccess && hashtags.data.length === 0 && <section className="empty empty-compact"><h2>Nessun hashtag disponibile</h2><p>Esegui i monitoraggi per raccogliere nuovi segnali.</p></section>}
        {hashtags.isSuccess && hashtags.data.length > 0 && <>{!allTags ? <><div className="hashtag-list">{hashtags.data.slice(0, 12).map((tag, index) => <Link key={tag.tag} className="hashtag-row" to={`/annunci?q=${encodeURIComponent(tag.tag)}`} state={{ from: '/trend?view=hashtags', backLabel: 'Radar hashtag' }}><span className="rank">{index + 1}</span><span className="hashtag-name"><strong>#{tag.tag}</strong><small>{numFmt.format(tag.authors)} autori · {tag.sources} {tag.sources === 1 ? 'fonte' : 'fonti'}</small></span><Momentum stage={tag.countPrev === 0 ? 'ACCELERATING' : tag.count7 > tag.countPrev ? 'RISING' : tag.count7 < tag.countPrev ? 'COOLING' : 'STEADY'} current={tag.count7} previous={tag.countPrev} /></Link>)}</div><button type="button" className="btn-secondary radar-more" onClick={() => setAllTags(true)}>Mostra tutti i dati</button></> : <><div className="tag-table" role="table" aria-label="Hashtag emergenti"><div className="tag-row tag-head" role="row"><span role="columnheader">Hashtag</span><span role="columnheader">7gg</span><span role="columnheader">Prima</span><span role="columnheader">Autori</span><span role="columnheader">Fonti</span></div>{hashtags.data.map((tag) => <Link key={tag.tag} className="tag-row" role="row" to={`/annunci?q=${encodeURIComponent(tag.tag)}`} state={{ from: '/trend?view=hashtags', backLabel: 'Radar hashtag' }}><span className="tag-name" role="cell">#{tag.tag}</span><span role="cell">{tag.count7}</span><span role="cell">{tag.countPrev}</span><span role="cell">{tag.authors}</span><span role="cell">{tag.sources}</span></Link>)}</div><button type="button" className="btn-secondary radar-more" onClick={() => setAllTags(false)}>Torna alla sintesi</button></>}</>}
      </section>}
    </div>
  );
}
